/**
 * The 120-yard field. At sprite scale a texture of the whole field would be 4000-8000 px wide (over
 * the iPhone texture limit, and tens of MB), so the field is one 10-yard tile, repeated: the stripe,
 * line and hash pattern repeats every 10 yards and mirrors about the middle of the field, so a top
 * and a bottom tile (each half the field tall) cover yards 0-100 as two TileSprites. End zones and
 * the goal lines are plain rectangles; yard numbers are a few static Text objects on top. Nothing is
 * re-tessellated per frame (the iPhone budget).
 */
import Phaser from 'phaser';
import { color, cssColor } from '../ui/tokens';
import type { FieldGeometry } from './geometry';

/** NFL hash marks sit 18'6" (6.17 yards) apart, centered. */
const HASH_HALF_SPACING_YARDS = 3.08;
/** Yard numbers sit about 12 yards in from each sideline, as on a real field. */
const NUMBER_INSET_YARDS = 12;
const PERIOD_YARDS = 10;
/** Yard-number size in CSS px at least, and as a share of a yard. */
const NUMBER_MIN_CSS_PX = 9;
const NUMBER_YARD_SHARE = 0.8;

export const FIELD_TEXTURE = 'field';

const thinLine = (ppy: number) => Math.max(1, Math.round(ppy / 10));

/**
 * One 10-yard tile, half the field tall: the top half, or the same drawn upside down for the
 * bottom. Kept in the texture manager (which outlives scene restarts); a new scale replaces it.
 */
function tile(scene: Phaser.Scene, geo: FieldGeometry, bottom: boolean): string {
  const { pxPerYard: ppy, height } = geo;
  const key = `${FIELD_TEXTURE}-${ppy}-${bottom ? 'bottom' : 'top'}`;
  if (scene.textures.exists(key)) return key;
  const w = PERIOD_YARDS * ppy;
  const h = Math.ceil(height / 2);
  // Draw the top half; the bottom tile is the same rectangles mirrored.
  const g = scene.make.graphics({}, false);
  const rect = (x: number, y: number, rw: number, rh: number) =>
    g.fillRect(x, bottom ? h - y - rh : y, rw, rh);

  g.fillStyle(color('field-grass'));
  rect(0, 0, w, h);
  g.fillStyle(color('field-grass-alt'));
  rect(0, 0, 5 * ppy, h);

  g.fillStyle(color('field-line'));
  const thin = thinLine(ppy);
  // Yard lines at 0 and 5, centered on the yard; the half left of 0 wraps to the tile's right edge.
  const half = Math.floor(thin / 2);
  rect(0, 0, thin - half, h);
  if (half > 0) rect(w - half, 0, half, h);
  rect(5 * ppy - half, 0, thin, h);
  // The sideline.
  rect(0, 0, w, thin);
  // Hash marks every yard but the fives: at the sideline and on the hash row.
  const tick = Math.max(2, Math.round(ppy / 2));
  const hashRow = Math.round(height / 2 - HASH_HALF_SPACING_YARDS * ppy) - Math.floor(tick / 2);
  for (let y = 1; y < PERIOD_YARDS; y++) {
    if (y % 5 === 0) continue;
    rect(y * ppy, 0, thin, tick);
    rect(y * ppy, hashRow, thin, tick);
  }

  g.generateTexture(key, w, h);
  g.destroy();
  return key;
}

/** Drop the tiles of any other scale. */
function forgetOtherScales(scene: Phaser.Scene, ppy: number): void {
  for (const k of scene.textures.getTextureKeys()) {
    if (k.startsWith(`${FIELD_TEXTURE}-`) && !k.startsWith(`${FIELD_TEXTURE}-${ppy}-`)) {
      scene.textures.remove(k);
    }
  }
}

export function drawField(
  scene: Phaser.Scene,
  geo: FieldGeometry,
): Phaser.GameObjects.GameObject[] {
  const { pxPerYard: ppy, endZoneYards, width, height, dpr } = geo;
  forgetOtherScales(scene, ppy);
  const xOf = (yard: number) => Math.round((yard + endZoneYards) * ppy);
  const half = Math.ceil(height / 2);
  const thin = thinLine(ppy);
  const line = color('field-line');
  const box = (x: number, y: number, w: number, h: number, c: number, depth = -10) =>
    scene.add.rectangle(x, y, w, h, c).setOrigin(0, 0).setDepth(depth);

  const out: Phaser.GameObjects.GameObject[] = [
    scene.add
      .tileSprite(xOf(0), 0, 100 * ppy, half, tile(scene, geo, false))
      .setOrigin(0, 0)
      .setDepth(-10),
    scene.add
      .tileSprite(xOf(0), height - half, 100 * ppy, half, tile(scene, geo, true))
      .setOrigin(0, 0)
      .setDepth(-10),
    // End zones, their sidelines and end lines, then the goal lines over the tiles' yard-0 lines.
    box(0, 0, xOf(0), height, color('field-endzone')),
    box(xOf(100), 0, width - xOf(100), height, color('field-endzone')),
    box(0, 0, xOf(0), thin, line, -9.5),
    box(0, height - thin, xOf(0), thin, line, -9.5),
    box(xOf(100), 0, width - xOf(100), thin, line, -9.5),
    box(xOf(100), height - thin, width - xOf(100), thin, line, -9.5),
    box(0, 0, thin, height, line, -9.5),
    box(width - thin, 0, thin, height, line, -9.5),
    box(xOf(0) - thin, 0, 2 * thin, height, line, -9.5),
    box(xOf(100) - thin, 0, 2 * thin, height, line, -9.5),
  ];
  const style: Phaser.Types.GameObjects.Text.TextStyle = {
    fontFamily: '"Press Start 2P"',
    fontSize: `${Math.max(Math.round(NUMBER_MIN_CSS_PX * dpr), Math.round(ppy * NUMBER_YARD_SHARE))}px`,
    color: cssColor('field-line'),
  };
  for (let y = 10; y <= 90; y += 10) {
    const label = String(y <= 50 ? y : 100 - y);
    for (const row of [NUMBER_INSET_YARDS, geo.widthYards - NUMBER_INSET_YARDS]) {
      out.push(
        scene.add
          .text(xOf(y), Math.round(row * ppy), label, style)
          .setOrigin(0.5)
          .setAlpha(0.7)
          .setDepth(-9),
      );
    }
  }
  return out;
}
